import { useState, useEffect } from 'react';
import { testFormula } from '../services/parameterService';
import { validateFormula } from '../services/agreementService';
import { Modal } from '../components/ui/Modal';
import { TextArea } from '../components/ui/TextArea';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';

interface AgreementFormulaTestDialogProps {
  isOpen: boolean;
  onClose: () => void;
  formula: string;
  formulaLabel: string;
  knownVariables?: string[];
}

interface SampleVariable {
  name: string;
  value: string;
}

export default function AgreementFormulaTestDialog({
  isOpen,
  onClose,
  formula,
  formulaLabel,
  knownVariables = [],
}: AgreementFormulaTestDialogProps) {
  const [formulaText, setFormulaText] = useState(formula);
  const [variables, setVariables] = useState<SampleVariable[]>([]);
  const [isTesting, setIsTesting] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [validationMsg, setValidationMsg] = useState<string | null>(null);

  useEffect(() => {
    // Match known variable names against the formula text
    // Variables use underscores (e.g., Gross_Bill_Amount) but formula uses spaces (e.g., Gross Bill Amount)
    // Sort longest first so "Gross_Bill_Amount" matches before "Gross_Bill"
    if (knownVariables.length > 0) {
      const formulaLower = formula.toLowerCase();
      const sorted = [...knownVariables].sort((a, b) => b.length - a.length);
      const referenced: string[] = [];
      let remaining = formulaLower;

      for (const name of sorted) {
        const spacedName = name.replace(/_/g, ' ').toLowerCase();
        // Use word boundary check to avoid partial matches
        const regex = new RegExp(`\\b${spacedName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
        if (regex.test(remaining)) {
          referenced.push(name);
          // Remove matched text so shorter substrings don't also match
          remaining = remaining.replace(regex, '');
        }
      }

      if (referenced.length > 0) {
        setVariables(referenced.map((name) => ({ name, value: '' })));
      } else {
        setVariables([{ name: '', value: '' }]);
      }
    } else {
      setVariables([{ name: '', value: '' }]);
    }
  }, [knownVariables, formula]);

  useEffect(() => {
    setFormulaText(formula);
  }, [formula]);

  const addVariable = () => {
    setVariables([...variables, { name: '', value: '' }]);
  };

  const removeVariable = (index: number) => {
    setVariables(variables.filter((_, i) => i !== index));
  };

  const updateVariable = (index: number, field: 'name' | 'value', val: string) => {
    const updated = [...variables];
    updated[index] = { ...updated[index], [field]: val };
    setVariables(updated);
  };

  const handleValidate = async () => {
    setError(null);
    setValidationMsg(null);
    setIsValidating(true);

    try {
      const res = await validateFormula({ formula: formulaText });
      if (res.valid) {
        setValidationMsg('Formula is valid.');
      } else {
        setError(`Validation errors: ${(res.errors || []).join('; ')}`);
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || 'Failed to validate formula.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleTest = async () => {
    setError(null);
    setResult(null);
    setValidationMsg(null);
    setIsTesting(true);

    try {
      const sampleValues: Record<string, unknown> = {};
      for (const v of variables) {
        if (v.name.trim()) {
          const num = Number(v.value);
          sampleValues[v.name.trim()] = isNaN(num) ? v.value : num;
        }
      }

      const res = await testFormula(formulaText, sampleValues);
      if (res.success) {
        setResult(`Result: ${JSON.stringify(res.result)}`);
      } else {
        setError(res.error || 'Formula test failed.');
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || 'Failed to test formula.');
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Test ${formulaLabel} Formula`} size="lg">
      <div className="parameter-test-dialog">
        <TextArea
          label={`${formulaLabel} Formula (Natural Language)`}
          value={formulaText}
          onChange={(e) => setFormulaText(e.target.value)}
          rows={3}
          placeholder="e.g., Gross_Bill minus Discount_Amount"
        />

        <div className="test-variables-section">
          <label className="test-variables-label">Sample Variable Values</label>
          {variables.map((v, i) => (
            <div key={i} className="test-variable-row">
              <Input
                placeholder="Variable name"
                value={v.name}
                onChange={(e) => updateVariable(i, 'name', e.target.value)}
              />
              <Input
                placeholder="Value"
                value={v.value}
                onChange={(e) => updateVariable(i, 'value', e.target.value)}
              />
              <Button variant="ghost" size="sm" onClick={() => removeVariable(i)}>
                ✕
              </Button>
            </div>
          ))}
          <Button variant="secondary" size="sm" onClick={addVariable}>
            + Add Variable
          </Button>
        </div>

        {error && (
          <Alert type="error" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}
        {validationMsg && (
          <Alert type="success" onDismiss={() => setValidationMsg(null)}>
            {validationMsg}
          </Alert>
        )}
        {result && (
          <Alert type="success" onDismiss={() => setResult(null)}>
            {result}
          </Alert>
        )}

        <div className="form-actions" style={{ marginTop: '1rem' }}>
          <Button
            onClick={handleValidate}
            variant="secondary"
            isLoading={isValidating}
            disabled={isValidating || isTesting || !formulaText.trim()}
          >
            Validate
          </Button>
          <Button
            onClick={handleTest}
            isLoading={isTesting}
            disabled={isTesting || isValidating || !formulaText.trim()}
          >
            Run Test
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
